import { getAsset, type AssetKey } from '@/assets/manifest';
import { cx } from '@/lib/cx';
import styles from './ResponsiveImage.module.css';

export interface ResponsiveImageProps {
  /** Clave del manifiesto de FORGE-BRAVO. Si no existe, es error de compilacion. */
  asset: AssetKey;
  /** Valor de `sizes`. Obligatorio: sin el, el navegador asume 100vw y se baja de mas. */
  sizes: string;
  /**
   * Texto alternativo. Por defecto el del manifiesto, que nunca esta vacio.
   * Solo se pasa cuando el CONTEXTO de la pagina dice algo mas util.
   */
  alt?: string;
  /** Imagen puramente decorativa: `alt=""` y fuera del arbol de accesibilidad. */
  decorative?: boolean;
  /** `eager` solo para lo que esta sobre el pliegue. */
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  className?: string;
}

/**
 * `<picture>` sobre el manifiesto: AVIF, WebP y fallback, con las anchuras
 * reales que hay en disco y las dimensiones intrinsecas puestas.
 *
 * Existe porque los tres FORGE de paginas habian escrito el mismo componente
 * por triplicado (peticion formal de FORGE-DELTA §2). Aqui vive UNA vez.
 *
 * DETALLE QUE CUESTA UNA TARDE: `<picture>` es `inline` por defecto y NO
 * transmite tamano a su `<img>`. Por eso el envoltorio se declara `block` y el
 * `<img>` lleva `width`/`height` intrinsecos: sin eso hay salto de layout, y
 * un hero con la imagen en una celda de rejilla de alto indefinido acaba
 * mandando su proporcion intrinseca y midiendo 2.858 px de alto (incidente
 * real de FORGE-ECHO en Cancun).
 */
export function ResponsiveImage({
  asset,
  sizes,
  alt,
  decorative = false,
  loading = 'lazy',
  fetchPriority,
  className,
}: ResponsiveImageProps) {
  const item = getAsset(asset);

  return (
    <picture className={cx(styles.picture, className)}>
      {item.sources.map((source) => (
        <source key={source.format} type={source.type} srcSet={source.srcSet} sizes={sizes} />
      ))}
      <img
        className={styles.img}
        src={item.src}
        /* Sin `fallbackSrcSet`, un navegador sin AVIF ni WebP se traga la
         * derivada mayor en un movil de 320 px. */
        srcSet={item.fallbackSrcSet}
        sizes={sizes}
        alt={decorative ? '' : (alt ?? item.alt)}
        width={item.width}
        height={item.height}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
      />
    </picture>
  );
}
